import { CURRENT_CLIENT } from '@/constants'
import { DEFAULT_CINEMATIC_HERO_HEADLINE_CLASS, DEFAULT_MARKETING_CONTENT_MAX_WIDTH } from '@/landing/marketing-layout-tokens'
import { resolveLandingCta } from '@/lib/resolve-jobs-board-url'
import type {
  ClientLandingConfig,
  LandingBrandSize,
  LandingHeaderStyle,
  LandingHeroConfig,
  LandingHeroVisualConfig,
  LandingLayoutPreset,
  LandingNavLink,
  LandingSection,
  LandingSurface,
} from '@/types/client-landing'

export type ClientWithLanding = typeof CURRENT_CLIENT & {
  siteTitle?: string
  name?: string
  tagline?: string
  logoPath?: string
  logoPathLight?: string
  logoPathDark?: string
  landing?: ClientLandingConfig
  chatSuggestions?: { valuePropositions?: string[] }
  theme?: { marketing?: Record<string, unknown> }
}

/** App tokens (default) or marketing block renderers — explicit config wins over theme inference. */
export function resolveLandingSurface(client: ClientWithLanding): LandingSurface {
  const explicit = client.landing?.surface
  if (explicit === 'app' || explicit === 'marketing') return explicit
  if (client.theme?.marketing && typeof client.theme.marketing === 'object') return 'marketing'
  return 'app'
}

/** Unauthenticated `/`: public landing (default) or redirect to `/login`. */
export function resolveGuestIndex(client: ClientWithLanding): 'login' | 'public' {
  return client.landing?.guestIndex === 'login' ? 'login' : 'public'
}

/** Hero band background — animation or image only; not logo / AnimatedTitle fallback. */
export function isCinematicBackgroundVisual(
  visual?: LandingHeroVisualConfig
): visual is Exclude<LandingHeroVisualConfig, { type: 'logo' }> {
  if (!visual) return false
  return visual.type === 'asciiAnimation' || visual.type === 'ascii' || visual.type === 'image'
}

export type ResolvedLandingChrome = {
  brandMode: 'animated' | 'static' | 'logo'
  brandSize: LandingBrandSize
  contentMaxWidth: number
  headerStyle: LandingHeaderStyle
  overlayInverse: boolean
  themeToggleOverlayTrack: boolean
}

export type ResolvedSplitHeroLayout = {
  stickyChrome: 'inset' | 'overlay'
  heroHeight: 'default' | '90svh'
  copyOffsetClass: string | null
  heroHeadlineClass: string
  heroOnImage: 'light' | 'theme'
}

export type ResolvedLanding = {
  preset: LandingLayoutPreset
  hero: LandingHeroConfig
  sections?: LandingSection[]
  landingComponent?: string
  transparentShell: boolean
  splitVisualPanel?: LandingHeroVisualConfig
  splitHeroMobileFullScreen: boolean
  splitHeroFullOpacityBackground: boolean
  splitHeroLayout: ResolvedSplitHeroLayout
  chrome: ResolvedLandingChrome
  brandTitle: string
  headerAuth: { signInLabel: string; registerLabel: string; hideRegister: boolean; hideSignIn: boolean }
  themeToggleEnabled: boolean
  navLinks?: LandingNavLink[]
  mobileNavCta?: LandingNavLink
  surface: LandingSurface
}

const CINEMATIC_HEADLINE_60PX_CLASS = 'text-3xl sm:text-5xl md:text-[60px]'
const CINEMATIC_COPY_OFFSET_18SVH_CLASS = 'md:translate-y-[18svh]'

function hasLogoAsset(client: ClientWithLanding): boolean {
  return Boolean(client.logoPath?.trim() || client.logoPathLight?.trim() || client.logoPathDark?.trim())
}

function resolveChrome(client: ClientWithLanding): ResolvedLandingChrome {
  const raw = client.landing?.chrome
  const logo = hasLogoAsset(client)
  const brandMode = raw?.brandMode ?? (logo ? 'logo' : 'animated')

  return {
    brandMode,
    brandSize: raw?.brandSize ?? '3xl',
    contentMaxWidth: raw?.contentMaxWidth ?? DEFAULT_MARKETING_CONTENT_MAX_WIDTH,
    headerStyle: raw?.headerStyle === 'floating' ? 'floating' : 'full',
    overlayInverse: raw?.overlayInverse === true,
    themeToggleOverlayTrack: raw?.themeToggleOverlayTrack === true,
  }
}

function resolveHeadlineClass(split: ClientLandingConfig['splitHero']): string {
  const typography = split?.heroTypography
  if (typography?.headlineClass) return typography.headlineClass
  if (typography?.headlineSize === '60px') return CINEMATIC_HEADLINE_60PX_CLASS
  return DEFAULT_CINEMATIC_HERO_HEADLINE_CLASS
}

function resolveCopyOffsetClass(split: ClientLandingConfig['splitHero']): string | null {
  if (split?.copyOffsetClass) return split.copyOffsetClass
  if (split?.copyOffsetPreset === '18svh') return CINEMATIC_COPY_OFFSET_18SVH_CLASS
  return null
}

function resolveSplitHeroLayout(
  raw: ClientLandingConfig | undefined,
  preset: LandingLayoutPreset
): ResolvedSplitHeroLayout {
  const split = raw?.splitHero
  const defaults: ResolvedSplitHeroLayout = {
    stickyChrome: 'inset',
    heroHeight: 'default',
    copyOffsetClass: null,
    heroHeadlineClass: DEFAULT_CINEMATIC_HERO_HEADLINE_CLASS,
    heroOnImage: 'theme',
  }

  if (preset !== 'splitHero') return defaults

  return {
    stickyChrome: split?.stickyChrome === 'overlay' ? 'overlay' : 'inset',
    heroHeight: split?.heroHeight === '90svh' ? '90svh' : 'default',
    copyOffsetClass: resolveCopyOffsetClass(split),
    heroHeadlineClass: resolveHeadlineClass(split),
    heroOnImage: split?.heroTypography?.onImage === 'light' ? 'light' : 'theme',
  }
}

export function resolveLanding(
  client: ClientWithLanding,
  marketingTheme?: 'light' | 'dark' | null
): ResolvedLanding {
  const raw = client.landing
  const siteTitle = client.siteTitle || client.name || 'Labs'
  const tagline = client.tagline || ''
  const preset = raw?.layoutPreset ?? 'centered'

  const valueProps = client.chatSuggestions?.valuePropositions
  const mobileFullScreen =
    preset === 'splitHero' && raw?.splitHero?.mobileFullScreen !== false
  const splitVisualPanel = isCinematicBackgroundVisual(raw?.splitHero?.visualPanel)
    ? raw?.splitHero?.visualPanel
    : undefined
  const splitHeroFullOpacityBackground = raw?.splitHero?.fullOpacityBackground === true
  const proofPoints =
    raw?.hero?.proofPoints ??
    (valueProps && valueProps.length > 0 ? valueProps.slice(0, 4) : undefined)

  const sections = raw?.sections
    ?.map((section) => {
      if (section.type === 'ctaBand') {
        const resolvedCta = resolveLandingCta(section.props.cta, marketingTheme)
        if (!resolvedCta) return null
        return { ...section, props: { ...section.props, cta: resolvedCta } }
      }
      if (section.type === 'splitNarrative' && section.props.cta) {
        const resolvedCta = resolveLandingCta(section.props.cta, marketingTheme)
        return {
          ...section,
          props: { ...section.props, cta: resolvedCta ?? section.props.cta },
        }
      }
      return section
    })
    .filter((section): section is LandingSection => section != null)

  return {
    preset,
    hero: {
      eyebrow: raw?.hero?.eyebrow,
      headline: raw?.hero?.headline ?? siteTitle,
      subhead: raw?.hero?.subhead ?? tagline,
      primaryCta:
        raw?.hero?.primaryCta === null
          ? undefined
          : resolveLandingCta(raw?.hero?.primaryCta ?? { label: 'Sign in', to: '/login' }, marketingTheme) ?? {
              label: 'Sign in',
              to: '/login',
            },
      secondaryCta:
        raw?.hero?.secondaryCta === null
          ? undefined
          : resolveLandingCta(raw?.hero?.secondaryCta, marketingTheme),
      visual: raw?.hero?.visual,
      proofPoints,
    },
    sections,
    landingComponent: raw?.landingComponent,
    transparentShell: raw?.transparentShell === true,
    splitVisualPanel,
    splitHeroMobileFullScreen: mobileFullScreen,
    splitHeroFullOpacityBackground,
    splitHeroLayout: resolveSplitHeroLayout(raw, preset),
    chrome: resolveChrome(client),
    brandTitle: siteTitle,
    headerAuth: {
      signInLabel: raw?.headerAuth?.signInLabel ?? 'Sign in',
      registerLabel: raw?.headerAuth?.registerLabel ?? 'Register',
      hideRegister: raw?.headerAuth?.hideRegister === true,
      hideSignIn: raw?.headerAuth?.hideSignIn === true,
    },
    themeToggleEnabled: raw?.themeToggleEnabled === true,
    navLinks: raw?.navLinks,
    mobileNavCta: raw?.mobileNavCta,
    surface: resolveLandingSurface(client),
  }
}
